/**
 * Battlepass. Bot sources: `rpg/battlepass/rewards.ts` (`getCost`,
 * `getFullLevel`, `getQuantity`, `openReward`, `claimAllRewards`),
 * `rpg/chest.ts#calculateChestRewards` and the nugget/page exchanges
 * (`rpg/play/button-routes.ts`). Premium accounts also claim the premium
 * track (parity P-15). The season reset is the bot's monthly job; the web
 * never resets the pass (P-14). `battlepass.level` is stored by quest
 * completion (`updateQuest`), as in the bot.
 */
import { BATTLEPASS, CHESTS, EXCHANGES } from '../balance.js';
import { battlepassData, type BattlepassItem } from '../data/index.js';
import { GameError } from '../errors.js';
import type { BattlepassGrant, GameEvent } from '../events.js';
import { premiumOf } from '../premium.js';
import { randInt, type Rng } from '../rng.js';
import type { BotUser } from '../state.js';

export type BattlepassTrack = 'free' | 'premium';

/** `getCost`: XP to go from tier `id − 1` to tier `id`. */
export function battlepassCost(id: number): number {
  const safe = id < 0 ? 0 : id;
  if (safe >= battlepassData.free.length) return BATTLEPASS.beyondListCost;
  const table = battlepassData.cost;
  return table[safe >= table.length ? table.length - 1 : safe] ?? 0;
}

/** `getFullLevel`: the highest tier the XP pays for (tier 0 is free from the start). */
export function battlepassLevelFor(xp: number): number {
  let cost = 0;
  let level = 0;
  for (let i = 1; cost <= xp; i++) {
    cost += battlepassCost(i);
    level++;
  }
  return level - 1;
}

/** Total XP needed to reach `level` (`getTotalCost`). */
export function battlepassXpFor(level: number): number {
  let cost = 0;
  for (let i = 0; i <= level; i++) cost += battlepassCost(i);
  return cost;
}

/** The reward of a tier on a track: the reward list, then the repeating bonus chest. */
export function battlepassTier(tier: number, track: BattlepassTrack = 'free'): BattlepassItem {
  return battlepassData[track][tier] ?? { ...battlepassData.bonus, id: tier };
}

/** When the bot's monthly job resets the pass (the 1st at midnight, server time) — display only, P-14. */
export function nextSeasonAt(now: number): number {
  const d = new Date(now);
  return new Date(d.getFullYear(), d.getMonth() + 1, 1, 0, 0, 0, 0).getTime();
}

/** The premium track is claimable on accounts with a stored premium tier (`claimAllRewards`). */
export function hasPremiumTrack(save: BotUser): boolean {
  return save.premium.level > 0;
}

export function claimableTiers(save: BotUser, track: BattlepassTrack = 'free'): number {
  return Math.max(0, save.battlepass.level - save.battlepass[track] + 1);
}

/** `getQuantity`: cash/special rewards, premium boosting the level term. */
export function rewardQuantity(item: BattlepassItem, playerLevel: number, premiumBossFakecoin = 0): number {
  const base = item.amount === 1 ? BATTLEPASS.base.one : BATTLEPASS.base.more;
  const levelTerm = 1 + (playerLevel / BATTLEPASS.levelDivisor) * (1 + premiumBossFakecoin / 100);
  if (item.type === 'cash') {
    switch (item.value) {
      case 'fakecoin':
        return Math.floor(base * BATTLEPASS.fakecoin * levelTerm);
      case 'lootcoin':
        return Math.floor(base * BATTLEPASS.lootcoin * levelTerm);
      case 'shopcoin':
        return BATTLEPASS.shopcoin;
      case 'magicalorb':
        return 1;
    }
  }
  if (item.type === 'special' && (item.value === 'shushupage' || item.value === 'icekamas')) return 1;
  return 0;
}

/** `calculateChestRewards`: variance roll, then nuggets; premium multiplies both currencies. */
export function rollChest(chest: string, playerLevel: number, rng: Rng, premiumBossFakecoin = 0): { money: number; lootbox: number; nuggets: number } {
  const m = CHESTS[chest] ?? { fakecoin: 1, lootcoin: 1, nuggets: 0 };
  const levelFactor = 1 + playerLevel / 25;
  const premiumFactor = 1 + premiumBossFakecoin / 100;
  const variance = 0.8 + rng.next() * 0.4;
  return {
    money: Math.floor(2500 * m.fakecoin * levelFactor * premiumFactor * variance),
    lootbox: Math.floor(2.5 * m.lootcoin * levelFactor * premiumFactor * variance),
    nuggets: m.nuggets > 0 ? randInt(rng, 1, m.nuggets + 1) : 0,
  };
}

function emptyGrant(tier: number, track: BattlepassTrack, item: BattlepassItem): BattlepassGrant {
  return { tier, track, type: item.type, value: item.value, money: 0, lootbox: 0, shopcoin: 0, nuggets: 0, magicalorb: 0, shushupage: 0, icekamas: 0 };
}

/**
 * Opens a chest (`openChest` phase 2): money, lootcoins and nuggets, and no
 * stat or quest counter (parity P-13 until T4.8).
 */
export function openChest(save: BotUser, chest: string, now: number, rng: Rng, events: GameEvent[]): { money: number; lootbox: number; nuggets: number } {
  const r = rollChest(chest, save.level.level, rng, premiumOf(save, now).boss.fakecoin);
  save.money += r.money;
  save.lootbox += r.lootbox;
  save.nuggets.current += r.nuggets;
  save.nuggets.total += r.nuggets;
  events.push({ type: 'chestOpened', chest, ...r });
  return r;
}

function grantTier(save: BotUser, tier: number, track: BattlepassTrack, now: number, rng: Rng, events: GameEvent[]): BattlepassGrant {
  const item = battlepassTier(tier, track);
  const g = emptyGrant(tier, track, item);
  if (item.type === 'chest') {
    const r = openChest(save, item.value, now, rng, events);
    g.money = r.money;
    g.lootbox = r.lootbox;
    g.nuggets = r.nuggets;
    return g;
  }
  const qty = rewardQuantity(item, save.level.level, premiumOf(save, now).boss.fakecoin);
  const s = save.stats.battlepass;
  switch (item.value) {
    case 'fakecoin':
      save.money += qty;
      s.fakecoin += qty;
      g.money = qty;
      break;
    case 'lootcoin':
      save.lootbox += qty;
      s.lootcoin += qty;
      g.lootbox = qty;
      break;
    case 'shopcoin':
      save.shopcoin += qty;
      s.shopcoin += qty;
      g.shopcoin = qty;
      break;
    case 'magicalorb':
      save.attributes.token.amount.available += qty;
      save.attributes.token.amount.total += qty;
      s.magicalorb += qty;
      g.magicalorb = qty;
      break;
    case 'shushupage':
      save.codex.pages += qty;
      s.shushupage += qty;
      g.shushupage = qty;
      break;
    case 'icekamas':
      save.icekamas.total += qty;
      save.icekamas.current += qty;
      s.icekamas += qty;
      g.icekamas = qty;
      break;
  }
  return g;
}

/**
 * `all`: `claimAllRewards` — every unlocked tier of the free track, then of
 * the premium track on premium accounts. Otherwise `openReward`: the next
 * tier of `track`.
 */
export function claimBattlepass(save: BotUser, opts: { all?: boolean; track?: BattlepassTrack }, now: number, rng: Rng, events: GameEvent[]): void {
  const level = save.battlepass.level;
  const rewards: BattlepassGrant[] = [];
  if (opts.all) {
    const tracks: BattlepassTrack[] = hasPremiumTrack(save) ? ['free', 'premium'] : ['free'];
    for (const track of tracks) {
      while (save.battlepass[track] <= level) {
        rewards.push(grantTier(save, save.battlepass[track], track, now, rng, events));
        save.battlepass[track] += 1;
      }
    }
  } else {
    const track = opts.track ?? 'free';
    if (track === 'premium' && !hasPremiumTrack(save)) throw new GameError('premiumOnly');
    if (save.battlepass[track] <= level) {
      rewards.push(grantTier(save, save.battlepass[track], track, now, rng, events));
      save.battlepass[track] += 1;
    }
  }
  if (rewards.length === 0) throw new GameError('battlepassLocked', { xp: battlepassXpFor(level + 1) });
  events.push({ type: 'battlepassClaimed', tiers: rewards.length, rewards });
}

/** 1000 nuggets → mythic chest; 6 Shushu pages → Shushu chest. */
export function exchange(save: BotUser, kind: keyof typeof EXCHANGES, now: number, rng: Rng, events: GameEvent[]): void {
  const ex = EXCHANGES[kind];
  if (kind === 'nuggets') {
    if (save.nuggets.current < ex.cost) throw new GameError('notEnoughNuggets', { have: save.nuggets.current, needed: ex.cost });
    save.nuggets.current -= ex.cost;
  } else {
    if (save.codex.pages < ex.cost) throw new GameError('notEnoughPages', { have: save.codex.pages, needed: ex.cost });
    save.codex.pages -= ex.cost;
  }
  openChest(save, ex.chest, now, rng, events);
}
